import clsx from 'clsx'
import ImageModalDsk from 'components/common/ImageModalDsk'
import ImageModalMbl from 'components/common/ImageModalMbl'
import IntLink from 'components/common/IntLink'
import LoadingOverlay from 'components/common/LoadingOverlay'
import PortfolioSlider from 'components/services/PortfolioSlider'
import { BLUR_DATA_URL, MEDIA_URL } from 'config/constants'
import useSlug from 'hooks/useSlug'
import { useKeenSlider } from 'keen-slider/react'
import { getServices } from 'lib/getServices'
import {
  isAbsent,
  isEmptyObj,
  isNonEmptyArray,
  isNonEmptyObj,
  isNonEmptyString,
  isPresent,
  isSame,
  toPairs,
} from 'lib/utils'
import Head from 'next/head'
import Image from 'next/image'
import { EventPageProps } from 'pages/catering/events/[slug]'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { type IServicePortfolio } from 'store/api'
import { selectIsLg, selectIsSm } from 'store/ui'

let EventView = ({ event: eventSSR }: EventPageProps) => {
  let { slug, queryLng } = useSlug()

  const isSm = useSelector(selectIsSm)
  const isLg = useSelector(selectIsLg)

  let [imgIdx, setImgIdx] = useState<number | null>(null)
  let [isMblModalOpen, setIsMblModalOpen] = useState(false)
  let [isDskModalOpen, setIsDskModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [_event, setEvent] = useState<IServicePortfolio>()
  const [groupedPortfolios, setGroupedPortfolios] = useState<
    {
      label: string
      items: IServicePortfolio[]
    }[]
  >([])

  let handleModalOpen = useCallback(
    (idx: number, type: 'dsk' | 'mbl') => {
      setImgIdx(idx)
      isSame(type, 'mbl')
        ? setIsMblModalOpen(true)
        : setIsDskModalOpen(true)
    },
    []
  )

  let [ref] = useKeenSlider<HTMLDivElement>({
    slides: {
      perView: 1.6,
      spacing: 8,
    },
  })

  useEffect(() => {
    const f = async () => {
      setLoading(true)
      const servicesData = await getServices(queryLng)
      setLoading(false)

      if (isPresent(servicesData)) {
        setGroupedPortfolios(
          toPairs(
            servicesData.portfolio.reduce((acc, cur) => {
              acc[cur.type_label] = (
                acc[cur.type_label] ?? []
              ).concat(cur)
              return acc
            }, {} as Record<IServicePortfolio['type_label'], IServicePortfolio[]>)
          ).map(([label, items]) => ({ label, items }))
        )

        setEvent(
          servicesData.portfolio.find((p) => isSame(p.slug, slug))
        )
      }
    }
    void f()
  }, [slug])

  let event = useMemo(
    () =>
      isNonEmptyObj(eventSSR) && isSame(eventSSR.slug, slug)
        ? eventSSR
        : _event,
    [eventSSR, slug, _event]
  )

  let images = event?.images.map((img) => ({
    src: `${MEDIA_URL}${img}`,
    height: 1,
    width: 1,
    blurDataURL: '',
  }))


  let text = isNonEmptyString(event?.text) ? (
    <>
      <div
        className={clsx('text-base2 leading-snug text-bodyGrey underscores')}
        dangerouslySetInnerHTML={{ __html: event!.text }}
      />
    </>
  ) : null

  if (loading)
    return (
      <main className='px-4 md:px-6 lg:px-8 xl:px-16 bg-white flex-grow py-64 relative'>
        <LoadingOverlay className='h-5 w-5 mx-2.5 bg-accent' open />
      </main>
    )

  if (isAbsent(event) || isEmptyObj(event))
    return (
      <div className='flex flex-col px-4'>
        <h1 className='mt-20 text-2xl xl:text-4xl text-center leading-snug'>
          Ошибка 404 <br /> Нет такой страницы
        </h1>
      </div>
    )

  return (
    <>
      <Head>
        <title>{event.name}</title>
      </Head>

      {isLg && (
        <div className='mt-4 py-4 pr-0 custom-px'>
          <div className='w-full'>
            <div className='hidden lg:block text-sm2 leading-tight tracking-tight'>
              <IntLink
                className='text-dark underline hover:text-accent'
                href='/'
              >
                Главная
              </IntLink>

              {' — '}
              <IntLink
                className='text-dark underline hover:text-accent'
                href='/catering'
              >
                Кейтеринг
              </IntLink>

              <span className='text-greyA1'>{` — ${event.name}`}</span>
            </div>
          </div>
        </div>
      )}

      <div className='mb-12 custom-px'>
        <div className='md:flex justify-between items-center'>
          <h1 className='mt-6 font-unbounded font-bold lg:font-medium text-xs uppercase lg:normal-case lg:text-5xl lg:leading-tight tracking-tight'>
            {event.name}
          </h1>
        </div>

        <div className='mt-5 lg:mt-8'>
          {!isSm && (
            <div className='flex flex-row justify-between'>
              <div className='w-[48%] mr-[5%]'>
                {text}

                <br />
                <div className='relative mt-2'>
                  <IntLink
                    className='btn-primary py-3 px-20 text-center text-sm font-semibold rounded'
                    href='/'
                  >
                    Перейти в меню
                  </IntLink>
                </div>
              </div>

              <div className='w-[47%] relative h-[377px] rounded-2xl overflow-hidden'>
                <Image
                  placeholder='blur'
                  blurDataURL={BLUR_DATA_URL}
                  src={`${MEDIA_URL}${event.preview}`}
                  alt=''
                  layout='fill'
                  objectFit='cover'
                />
              </div>
            </div>
          )}

          {isSm && (
            <div className='border-b pb-5 border-b-lightGrey'>
              <div className='relative aspect-h-2 aspect-w-3 rounded-lg overflow-hidden mb-7'>
                <Image
                  placeholder='blur'
                  blurDataURL={BLUR_DATA_URL}
                  src={`${MEDIA_URL}${event.preview}`}
                  alt=''
                  layout='fill'
                  objectFit='cover'
                />
              </div>

              {text}

              {isNonEmptyArray(event.images) && (
                <div className='keen-slider z-10 mb-5.5' ref={ref}>
                  {event.images.map((pic, idx) => (
                    <div key={pic} className='keen-slider__slide'>
                      <div className='aspect-w-3 aspect-h-2 order-1 rounded-lg overflow-hidden'>
                        <Image
                          onClick={() => handleModalOpen(idx, 'mbl')}
                          placeholder='blur'
                          blurDataURL={BLUR_DATA_URL}
                          src={`${MEDIA_URL}${pic}`}
                          alt=''
                          layout='fill'
                          objectFit='cover'
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className='relative rounded-xl'>
                <div className='flex flex-row justify-center'>
                  <IntLink
                    className='btn-primary py-3 px-4 w-full text-center text-xs md:text-sm font-semibold rounded-lg'
                    href='/'
                  >
                    Перейти в меню
                  </IntLink>
                </div>
              </div>
            </div>
          )}
        </div>

        {!isSm && isNonEmptyArray(event.images) && (
          <div className='border-t border-b border-lightGrey mt-16 py-10 grid grid-cols-3 gap-x-16 gap-y-10'>
            {event.images.map((pic, idx) => (
              <div key={pic}>
                <div className='relative aspect-w-3 aspect-h-2 cursor-zoom-in rounded-2xl overflow-hidden'>
                  <Image
                    onClick={() => handleModalOpen(idx, 'dsk')}
                    placeholder='blur'
                    blurDataURL={BLUR_DATA_URL}
                    src={`${MEDIA_URL}${pic}`}
                    alt=''
                    layout='fill'
                    objectFit='cover'
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isNonEmptyArray(groupedPortfolios) && (
        <div className='custom-pl -mt-4 lg:mt-12 mb-8 lg:mb-20 border-b pb-5 border-b-lightGrey lg:border-none'>
          {groupedPortfolios?.sort((a,b) => {
            if (a.label == event?.type_label) return -1
            if (b.label == event?.type_label) return 1
            return 0
          })?.map((section) => (
            <PortfolioSlider key={section.label} section={section} />
          ))}
        </div>
      )}

      <ImageModalDsk
        images={images}
        idx={imgIdx ?? 0}
        src={event.images[imgIdx ?? 0]!}
        isOpen={isDskModalOpen}
        setIsOpen={setIsDskModalOpen}
      />

      <ImageModalMbl
        src={event.images[imgIdx ?? 0]!}
        idx={imgIdx ?? 0}
        images={images}
        isOpen={isMblModalOpen}
        setIsOpen={setIsMblModalOpen}
      />
    </>
  )
}

export default EventView
